import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import Moment from 'moment';
import Styled from 'styled-components/macro';
import Util from '../shared/util/util';
import UpdateClientMutation from '../../mutations/update_client_mutation';
import {RichTextEditorDraftJsWrapper} from '../shared/components/rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';

// Design tokens (Figma: Company Viewscreen — sky design system)
// sky/sys/color/text/content/main
const COLOR_TEXT_MAIN = '#232334';
// sky/sys/color/text/content/primary (links + low-emphasis buttons)
const COLOR_TEXT_PRIMARY = '#0004db';
// sky/sys/color/text/content/secondary (low-emphasis meta text, e.g. "Updated: …")
const COLOR_TEXT_SECONDARY = '#6b6d7b';
// sky/sys/color/border/decorative
const COLOR_BORDER = '#d3d3df';
// sky/sys/color/background/neutral/1
const COLOR_SURFACE = '#ffffff';
// sky/sys/color/background/neutral/2
const COLOR_SURFACE_RAISED = '#fcfbfd';

// IGNITE-479 is a shell: the Guest Users (Main) and Notes panels are rendered in their EMPTY-STATE
// variation only. Populated data + the Contacts-module variant are wired in later tickets.
const Layout = Styled.div`
	display: flex;
	flex: 1 1 auto;
	align-items: stretch;
	/* 32px gap between the header/info bar and the content (Figma: header ends y=152, content y=184). */
	margin-top: 32px;
	border-top: 1px solid ${COLOR_BORDER};
`;

const MainColumn = Styled.div`
	flex: 1 1 auto;
	min-width: 0;
	padding: 16px 32px 32px 32px;
`;

const SectionHeader = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 44px;
	margin-bottom: 16px;
`;

// sky/sys/font/heading/4 — 20/24 medium
const SectionTitle = Styled.h2`
	font-size: 20px;
	font-weight: 500;
	line-height: 24px;
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

const CreateButton = Styled.button`
	display: inline-flex;
	align-items: center;
	height: 44px;
	padding: 0 16px;
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${COLOR_SURFACE};
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${COLOR_TEXT_MAIN};
	cursor: pointer;
`;

// Shared empty-state card used by both the Guest Users and Notes panels.
const EmptyCard = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 240px;
	padding: 24px;
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${COLOR_SURFACE};
`;

const EmptyContent = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 331px;
	text-align: center;
`;

// sky/sys/font/body/1 — 16/24 book
const EmptyMessage = Styled.p`
	margin: 0;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${COLOR_TEXT_MAIN};
`;

// Low-emphasis button (sky/comp/button low emphasis): blue text, no chrome.
const EmptyActionButton = Styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	padding: 0 12px;
	border: none;
	background: none;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${COLOR_TEXT_PRIMARY};
	cursor: pointer;
`;

const NotesPanel = Styled.aside`
	display: flex;
	flex-direction: column;
	flex: 0 0 512px;
	width: 512px;
	border-left: 1px solid ${COLOR_BORDER};
`;

const NotesHeader = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	height: 60px;
	padding: 0 16px 0 24px;
	background-color: ${COLOR_SURFACE_RAISED};
	border-bottom: 1px solid ${COLOR_BORDER};
`;

// sky/sys/font/label/1 — 16/24 medium
const NotesTitle = Styled.h2`
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

const NotesCollapse = Styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	background: none;
	font-size: 16px;
	line-height: 1;
	color: ${COLOR_TEXT_MAIN};
	cursor: pointer;
`;

const NotesBody = Styled.div`
	padding: 16px;
`;

const NotesHeaderRight = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// sky/sys/font/body/2 — 14/20 book, low-emphasis meta text
const UpdatedText = Styled.span`
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	color: ${COLOR_TEXT_SECONDARY};
	white-space: nowrap;
`;

// Rotates the chevron when the panel is collapsed.
const Chevron = Styled.span<{$collapsed: boolean}>`
	display: inline-block;
	transition: transform 0.15s ease;
	transform: rotate(${({$collapsed}) => ($collapsed ? '180deg' : '0deg')});
`;

// Bordered, scrollable container for the inline rich-text editor (Figma: notes text box).
const NotesEditorCard = Styled.div`
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${COLOR_SURFACE};
	padding: 8px 12px;
	.editor {
		margin: 0;
	}
`;

type CompanyViewOverviewProps = {
	clientId: string;
	notes: string | null;
	notesUpdatedAt: string | null;
};

const formatUpdatedAt = (value: string): string => Moment.utc(value).local().format('MMM D, YYYY, [@] h:mma');

// True for null/empty content and for a draft-js doc that has no text and no entities (e.g. the
// editor was opened via "Create Note" and blurred without typing). Prevents persisting an empty note.
const isBlankNotes = (content: string | null): boolean => {
	if (!content || content.trim() === '') {
		return true;
	}
	try {
		const parsed = JSON.parse(content);
		if (parsed && Array.isArray(parsed.blocks)) {
			const hasText = parsed.blocks.some((block: {text?: string}) => (block.text ?? '').trim() !== '');
			const hasEntities = parsed.entityMap && Object.keys(parsed.entityMap).length > 0;
			return !hasText && !hasEntities;
		}
	} catch {
		// Not draft-js JSON — fall through to a tag-stripped check.
	}
	return (
		content
			.replace(/<[^>]*>/g, '')
			.replace(/&nbsp;/g, ' ')
			.trim() === ''
	);
};

const CompanyViewOverview: React.FC<CompanyViewOverviewProps> = ({clientId, notes, notesUpdatedAt}) => {
	const {formatMessage} = useIntl();
	const [collapsed, setCollapsed] = useState(false);
	// Empty-state "Create Note" reveals the editor inline before any content exists.
	const [creating, setCreating] = useState(false);

	// Blank content (null or an empty rich-text doc) shows the empty state, not an empty editor.
	const showEditor = !isBlankNotes(notes) || creating;

	// Save on blur — the RTE wrapper fires onDraftJsChanged with the serialized content when the
	// editor loses focus.
	const handleNotesBlur = (newNotes: string): void => {
		// Blurred while blank → drop back to the empty state.
		if (isBlankNotes(newNotes)) {
			setCreating(false);
		}
		// Unchanged content → nothing to save.
		if ((notes ?? '') === (newNotes ?? '')) {
			return;
		}
		// Empty editor over an already-empty note (Create Note → blur with no input) → nothing to persist.
		if (isBlankNotes(notes) && isBlankNotes(newNotes)) {
			return;
		}
		Util.CommitMutation(UpdateClientMutation, {id: clientId, notes: newNotes});
	};

	return (
		<Layout>
			<MainColumn>
				<SectionHeader>
					<SectionTitle>{formatMessage({id: 'company_view_page.guest_users'})}</SectionTitle>
					<CreateButton type="button">{formatMessage({id: 'company_view_page.create_guest_user'})}</CreateButton>
				</SectionHeader>
				<EmptyCard>
					<EmptyContent>
						<EmptyMessage>{formatMessage({id: 'company_view_page.guest_users_empty'})}</EmptyMessage>
						<EmptyActionButton type="button">
							{formatMessage({id: 'company_view_page.create_guest_user'})}
						</EmptyActionButton>
					</EmptyContent>
				</EmptyCard>
			</MainColumn>

			<NotesPanel data-cy="company-view-notes-panel">
				<NotesHeader>
					<NotesTitle>{formatMessage({id: 'company_view_page.notes_title'})}</NotesTitle>
					<NotesHeaderRight>
						{notesUpdatedAt && (
							<UpdatedText data-cy="company-view-notes-updated">
								{formatMessage(
									{id: 'company_view_page.notes_updated_at'},
									{date: formatUpdatedAt(notesUpdatedAt)}
								)}
							</UpdatedText>
						)}
						<NotesCollapse
							type="button"
							data-cy="company-view-notes-collapse"
							aria-expanded={!collapsed}
							aria-label={formatMessage({
								id: collapsed ? 'company_view_page.notes_expand' : 'company_view_page.notes_collapse',
							})}
							onClick={() => setCollapsed(prev => !prev)}
						>
							<Chevron $collapsed={collapsed} aria-hidden="true">
								{'⌃'}
							</Chevron>
						</NotesCollapse>
					</NotesHeaderRight>
				</NotesHeader>
				{!collapsed && (
					<NotesBody>
						{showEditor ? (
							<NotesEditorCard>
								<RichTextEditorDraftJsWrapper
									// Re-seed the uncontrolled editor when the saved content changes (e.g. external update).
									key={notesUpdatedAt ?? 'new'}
									id={`company-view-notes-${clientId}`}
									data-cy="company-view-notes-editor"
									initialDraftJsContent={notes ?? ''}
									onDraftJsChanged={handleNotesBlur}
									placeholder={formatMessage({id: 'company_view_page.notes_placeholder'})}
									minHeight="240px"
									maxHeight="max(240px, calc(100vh - 360px))"
								/>
							</NotesEditorCard>
						) : (
							<EmptyCard>
								<EmptyContent>
									<EmptyMessage>{formatMessage({id: 'company_view_page.notes_empty'})}</EmptyMessage>
									<EmptyActionButton
										type="button"
										data-cy="company-view-notes-create"
										onClick={() => setCreating(true)}
									>
										{formatMessage({id: 'company_view_page.create_note'})}
									</EmptyActionButton>
								</EmptyContent>
							</EmptyCard>
						)}
					</NotesBody>
				)}
			</NotesPanel>
		</Layout>
	);
};

export default CompanyViewOverview;
